// Vendor
import { FC } from 'react';
import { configVars } from 'src/helpers';

// Components
import Button, {
  ButtonDisabledStyle,
  ButtonSize,
  ButtonVariant
} from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import Table from 'src/components/atoms/Table';
import TableBody from 'src/components/atoms/TableBody';
import TableCell from 'src/components/atoms/TableCell';
import TableContainer from 'src/components/atoms/TableContainer';
import TableHead from 'src/components/atoms/TableHead';
import TableRow from 'src/components/atoms/TableRow';
import { TextColor, TextVariant } from 'src/components/atoms/Text';
import ToolTip, { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { Content, Header, HeaderInfo, StyledText } from './styles';

// Constants
import { StyledTag } from 'src/components/organisms/IdentityVerificationRow/styles';
import { parseTimeDistance, parseTimeToYear } from 'src/helpers';
import { IdentityVerificationResult, RoleType } from 'src/ts/interfaces';
import { getReadableFailReason } from '../IdentityVerificationLinkRow/helpers';
import { getColumns } from './constants';

export interface IdentityVerificationRowProps {
  result: IdentityVerificationResult | null;
  role: RoleType;
}

const BasicInfo = ({ name, company }: { name: string; company: string }) => {
  return (
    <Header>
      <HeaderInfo>
        <Icon icon="person-icon" color="gray900" />
        <StyledText variant={TextVariant.h4} color={TextColor.gray700}>
          {name}
        </StyledText>
      </HeaderInfo>
      <HeaderInfo>
        <Icon icon="company-model-icon" color="gray700" />
        <StyledText name="property-name" variant={TextVariant.h4} color={TextColor.gray700}>
          {company}
        </StyledText>
      </HeaderInfo>
    </Header>
  );
};

const IdentityVerificationRow: FC<IdentityVerificationRowProps> = ({ result }) => {
  const isIncompleteStatus = result?.status === 'INCOMPLETE';
  const columns = getColumns(isIncompleteStatus);

  const getDisplayName = (result: IdentityVerificationResult | null): string => {
    if (!result) return 'No name provided';

    const fullName = `${result.firstName ?? ''} ${result.lastName ?? ''}`.trim();
    if (fullName) return fullName;
    if (result.email) return result.email;
    if (result.phone) return result.phone;
    return 'No name provided';
  };

  return (
    <Content>
      <BasicInfo name={getDisplayName(result)} company={result?.property.name ?? ''} />

      <TableContainer style={{ overflowX: 'visible' }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map(({ label, width, name }) => (
                <TableCell key={name} width={width}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key={result?.id}>
              {columns.map(({ name }) => {
                switch (name) {
                  case 'linkCreated':
                    return (
                      <TableCell key={name}>
                        {parseTimeDistance(result?.sessionCreatedAt ?? '')}
                      </TableCell>
                    );
                  case 'resultsReceived':
                    return (
                      <TableCell key={name}>
                        {parseTimeDistance(result?.sessionClosedAt ?? '')}
                      </TableCell>
                    );
                  case 'expirationDate':
                    return (
                      <TableCell key={name}>
                        {result?.licenseExpiresAt
                          ? parseTimeToYear(result.licenseExpiresAt)
                          : 'N/A'}
                      </TableCell>
                    );
                  case 'result':
                    return (
                      <TableCell key={name}>
                        {result?.status && <StyledTag label={result.status} />}
                      </TableCell>
                    );
                  case 'reason':
                    return (
                      <TableCell key={name} style={{ overflow: 'visible' }}>
                        {result?.failReason ? (
                          <ToolTip
                            direction={ToolTipDirection.top}
                            content={<div>{getReadableFailReason(result?.failReason)}</div>}
                          >
                            <StyledText
                              variant={TextVariant.small}
                              color={TextColor.secondary}
                              style={{
                                display: 'inline-block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {getReadableFailReason(result?.failReason)}
                            </StyledText>
                          </ToolTip>
                        ) : (
                          <StyledText
                            variant={TextVariant.small}
                            color={TextColor.secondary}
                            style={{
                              display: 'inline-block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {'N/A'}
                          </StyledText>
                        )}
                      </TableCell>
                    );
                  default:
                    return null;
                }
              })}

              {!isIncompleteStatus && (
                <TableCell>
                  <Button
                    onClick={() =>
                      window.open(
                        `${configVars.epa_url}/identity/applicants/${result?.id}/report?format=html&preset=summary-and-documents`,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    name="view_report_button"
                    variant={ButtonVariant.ghost}
                    disabledStyle={ButtonDisabledStyle.transparent}
                    size={ButtonSize.normal}
                  >
                    View&nbsp;&nbsp;
                    <Icon icon="document-search-icon" color="blue700" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Content>
  );
};

export default IdentityVerificationRow;
